/**
 * Diagnostics tab — Basic Plan + C-Suite. Renders the RAG-vocabulary
 * signal panel (Schedule slippage, Budget overrun, Critical-path delay,
 * Go-live at risk …) plus the critical-path strip. Every signal names
 * the exact items responsible so the exec can act immediately.
 */
import React, { useEffect, useState } from "react";
import { useProject } from "../../state/ProjectContext";
import { api } from "../../lib/api";
import {
  HeartPulse, AlertTriangle, Flame, TrendingDown, GitBranch, UserX,
  ShieldAlert, MegaphoneIcon, Rocket, Sparkles, Wrench, LifeBuoy,
  ArrowRight, Loader2, CheckCircle2, X, Bot,
} from "lucide-react";

const ICONS = {
  SCHEDULE_SLIPPAGE:   TrendingDown,
  BUDGET_OVERRUN:      Flame,
  SCOPE_AT_RISK:       AlertTriangle,
  CRITICAL_PATH_DELAY: GitBranch,
  RESOURCE_CONSTRAINT: UserX,
  HIGH_RISK_EXPOSURE:  ShieldAlert,
  ISSUE_ESCALATION:    MegaphoneIcon,
  GO_LIVE_AT_RISK:     Rocket,
  BENEFITS_AT_RISK:    Sparkles,
  RECOVERY_MODE:       LifeBuoy,
  REMEDIATION_PLAN:    Wrench,
};

const SEVERITY_STYLES = {
  Red:   { chip: "bg-red-100 text-red-700 border-red-200",       dot: "bg-red-500",     tint: "bg-red-50 border-red-200" },
  Amber: { chip: "bg-amber-100 text-amber-700 border-amber-200", dot: "bg-amber-500",   tint: "bg-amber-50 border-amber-200" },
  Green: { chip: "bg-emerald-100 text-emerald-700 border-emerald-200", dot: "bg-emerald-500", tint: "bg-emerald-50 border-emerald-200" },
};

function OverallBadge({ status, reds, ambers }) {
  const s = SEVERITY_STYLES[status] || SEVERITY_STYLES.Green;
  return (
    <div className={`rounded-2xl border p-4 flex items-center gap-4 ${s.tint}`} data-testid="diagnostics-overall">
      <div className={`w-14 h-14 rounded-full grid place-items-center ${s.dot} text-white shadow-lg`}>
        <HeartPulse size={22} />
      </div>
      <div className="flex-1">
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">Overall status</div>
        <div className="font-display text-2xl font-extrabold text-zinc-900">RAG {status}</div>
        <div className="text-[12px] text-zinc-600 mt-1">
          <span className="font-semibold text-red-700">{reds}</span> red · <span className="font-semibold text-amber-700">{ambers}</span> amber
        </div>
      </div>
    </div>
  );
}

function CriticalPathStrip({ chain }) {
  if (!chain?.length) return null;
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid="critical-path-strip">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">Critical Path</div>
          <div className="font-display text-lg font-bold text-zinc-900 mt-1">
            {chain.length} nodes on the delivery-critical chain
          </div>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-widest text-red-600 px-2 py-1 rounded-full bg-red-50 border border-red-200">
          CRIT
        </span>
      </div>
      <div className="flex items-center gap-2 overflow-x-auto pb-2" data-testid="critical-path-nodes">
        {chain.map((n, i) => {
          const pct = Number(n.pct ?? -1);
          const done = pct >= 100 || (n.status || "").toLowerCase() === "completed";
          const late = !done && n.days_late > 0;
          const dot = done ? "bg-emerald-500" : late ? "bg-red-500" : "bg-amber-400";
          return (
            <React.Fragment key={(n.id || i) + "_" + i}>
              <div
                className="min-w-[190px] max-w-[210px] rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 shrink-0 relative"
                data-testid={`crit-node-${i}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className={`w-2 h-2 rounded-full ${dot}`} />
                  <span className="font-mono text-[10px] text-zinc-500 uppercase">{n.type}</span>
                </div>
                <div className="text-[13px] font-semibold text-zinc-900 truncate">{n.name}</div>
                <div className="text-[11px] text-zinc-500 mt-1">
                  {n.end || n.date || "—"}
                  {pct >= 0 && <span> · {pct}%</span>}
                </div>
              </div>
              {i < chain.length - 1 && <ArrowRight size={14} className="text-zinc-300 shrink-0" />}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function ResponsibleRow({ item }) {
  // Compact "who/what is responsible" line — adapts to item shape.
  const bits = [];
  if (item.name || item.title) bits.push(<b key="n">{item.name || item.title}</b>);
  if (item.category)  bits.push(item.category);
  if (item.severity)  bits.push(<span key="s" className="font-mono text-[10px] uppercase tracking-wider text-red-700">{item.severity}</span>);
  if (item.days_late) bits.push(<span key="d" className="text-red-600">{item.days_late}d late</span>);
  if (item.overspend) bits.push(<span key="o" className="text-red-600">−${Math.round(item.overspend).toLocaleString()}</span>);
  if (item.owner)     bits.push(<span key="ow" className="text-zinc-500">· {item.owner}</span>);
  if (item.issue)     bits.push(<span key="i" className="text-amber-700">· {item.issue}</span>);
  if (item.why)       bits.push(<span key="w" className="text-red-600">· {item.why}</span>);
  return (
    <li className="text-[13px] text-zinc-800 py-1.5 flex items-center gap-2 flex-wrap">
      <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 shrink-0" />
      {bits.map((b, i) => <span key={i}>{b}{i < bits.length-1 ? " " : ""}</span>)}
    </li>
  );
}

function SignalCard({ signal }) {
  const s = SEVERITY_STYLES[signal.severity] || SEVERITY_STYLES.Amber;
  const Icon = ICONS[signal.code] || AlertTriangle;
  return (
    <div className={`rounded-2xl border p-5 bg-white`} data-testid={`signal-${signal.code}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-lg grid place-items-center ${s.chip} border`}>
          <Icon size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-base font-bold text-zinc-900 truncate">{signal.label}</div>
          <div className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full mt-1 border ${s.chip}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} /> {signal.severity}
          </div>
        </div>
      </div>
      <div className="text-[13px] text-zinc-700 mb-2 font-medium">{signal.trigger}</div>
      {signal.responsible?.length > 0 && (
        <>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-3 mb-1">Responsible items</div>
          <ul className="divide-y divide-zinc-100">
            {signal.responsible.map((it, i) => <ResponsibleRow key={i} item={it} />)}
          </ul>
        </>
      )}
    </div>
  );
}

export default function Diagnostics() {
  const { doc, replaceDoc } = useProject();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [remOpen, setRemOpen] = useState(false);
  const [drafts, setDrafts] = useState(null);
  const [draftLoading, setDraftLoading] = useState(false);
  const [draftErr, setDraftErr] = useState("");
  const [selected, setSelected] = useState({});
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(0);

  useEffect(() => {
    if (!doc?.id) return;
    let alive = true;
    setData(null); setErr("");
    api.projectDiagnostics(doc.id)
      .then(d => { if (alive) setData(d); })
      .catch(e => { if (alive) setErr(e?.response?.data?.detail || e.message || "Failed to load"); });
    return () => { alive = false; };
  }, [doc?.id, accepted]);

  const openRemediation = async () => {
    setRemOpen(true); setDrafts(null); setDraftErr(""); setDraftLoading(true);
    try {
      const r = await api.remediationDraft(doc.id);
      setDrafts(r.drafts || []);
      // Preselect every draft.
      const sel = {}; (r.drafts || []).forEach((_, i) => sel[i] = true);
      setSelected(sel);
    } catch (e) {
      setDraftErr(e?.response?.data?.detail || e.message || "Failed to draft");
    } finally {
      setDraftLoading(false);
    }
  };

  const acceptSelected = async () => {
    const items = drafts.filter((_, i) => selected[i]);
    if (items.length === 0) return;
    setAccepting(true);
    try {
      const r = await api.remediationAccept(doc.id, items);
      setAccepted(prev => prev + r.added);
      setRemOpen(false);
      // Nudge the ProjectContext to refetch so Actions tab shows the new rows.
      if (typeof replaceDoc === "function") {
        try {
          const fresh = await (await fetch(`/api/projects/${doc.id}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("pmv5_csuite_token")}` },
          })).json();
          replaceDoc(fresh);
        } catch { /* non-fatal */ }
      }
    } catch (e) {
      setDraftErr(e?.response?.data?.detail || e.message || "Failed to accept");
    } finally {
      setAccepting(false);
    }
  };

  if (err) return (
    <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-700" data-testid="diagnostics-error">
      Failed to load diagnostics: {err}
    </div>
  );
  if (!data) return (
    <div className="rounded-2xl border border-zinc-100 bg-white p-6 text-sm text-zinc-500 flex items-center gap-2" data-testid="diagnostics-loading">
      <Loader2 size={14} className="animate-spin" /> Analysing project health…
    </div>
  );

  return (
    <div className="space-y-6" data-testid="diagnostics-tab">
      {accepted > 0 && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-900 flex items-center gap-2" data-testid="diagnostics-accepted-banner">
          <CheckCircle2 size={16} /> {accepted} remediation action{accepted !== 1 ? "s" : ""} added to the Actions tab. The green REMEDIATION_PLAN signal will now show.
        </div>
      )}
      {/* Header row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <OverallBadge status={data.overall_status} reds={data.reds} ambers={data.ambers} />
        <div className="rounded-2xl border border-zinc-200 bg-white p-4 lg:col-span-2 flex items-start gap-3" data-testid="diagnostics-legend">
          <HeartPulse size={16} className="text-violet-600 shrink-0 mt-1" />
          <div className="flex-1 min-w-0">
            <div className="text-[13px] text-zinc-700 leading-relaxed">
              <b>Executive vocabulary in use:</b> RAG status · Critical-path delay · Schedule slippage · Budget overrun · Scope at risk · Resource constraint · High-risk exposure · Issue escalation · Go-live at risk · Benefits realization at risk · Recovery mode · Remediation plan.
              <span className="text-zinc-500"> Each firing signal names the exact items responsible below.</span>
            </div>
            {data.reds > 0 && (
              <button
                onClick={openRemediation}
                className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-violet-600 to-blue-600 text-white text-[12px] font-semibold hover:opacity-95 transition-opacity"
                data-testid="btn-ai-remediation"
              >
                <Bot size={14} /> Auto-draft Remediation Plan with Claude
              </button>
            )}
          </div>
        </div>
      </div>

      <CriticalPathStrip chain={data.critical_path} />

      {data.signals.length === 0 ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center" data-testid="diagnostics-clean">
          <CheckCircle2 size={22} className="text-emerald-600 mx-auto mb-2" />
          <div className="font-display text-lg font-bold text-emerald-900">All clear</div>
          <div className="text-sm text-emerald-800 mt-1">No distress signals firing on this project.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="diagnostics-signals-grid">
          {data.signals.map(s => <SignalCard key={s.code} signal={s} />)}
        </div>
      )}

      {/* ── AI Remediation Modal ────────────────────────────────────────── */}
      {remOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="remediation-modal">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between bg-gradient-to-r from-violet-600 to-blue-600 text-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-white/20 grid place-items-center">
                  <Bot size={20} />
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest opacity-80">Claude · Auto-drafted</div>
                  <div className="font-display text-lg font-bold">Remediation Plan</div>
                </div>
              </div>
              <button onClick={() => setRemOpen(false)} className="text-white/80 hover:text-white" data-testid="btn-close-remediation">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {draftLoading && (
                <div className="text-sm text-zinc-500 py-8 text-center flex items-center justify-center gap-2" data-testid="remediation-loading">
                  <Loader2 size={16} className="animate-spin" /> Drafting one corrective action per red signal…
                </div>
              )}
              {draftErr && !draftLoading && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700" data-testid="remediation-error">
                  {draftErr}
                </div>
              )}
              {drafts && drafts.length === 0 && !draftLoading && (
                <div className="text-sm text-zinc-500 py-8 text-center">No red signals to remediate. 🎉</div>
              )}
              {drafts && drafts.length > 0 && !draftLoading && (
                <>
                  <div className="text-[13px] text-zinc-600 mb-4">
                    Review each proposed action. Untick anything you don&apos;t want. Accepted actions land in the Actions tab tagged <span className="font-mono text-[11px] bg-zinc-100 px-1.5 py-0.5 rounded">Remediation</span> and light the green REMEDIATION_PLAN signal on Diagnostics.
                  </div>
                  <div className="space-y-3">
                    {drafts.map((d, i) => (
                      <label
                        key={i}
                        className={`block rounded-xl border p-4 cursor-pointer transition-colors ${selected[i] ? "border-violet-500 bg-violet-50/30" : "border-zinc-200"}`}
                        data-testid={`remediation-draft-${i}`}
                      >
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            className="mt-1 accent-violet-600"
                            checked={!!selected[i]}
                            onChange={(e) => setSelected(prev => ({ ...prev, [i]: e.target.checked }))}
                            data-testid={`remediation-draft-checkbox-${i}`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span className="font-mono text-[10px] uppercase tracking-widest text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full">
                                {d.signal_label || d.signal_code}
                              </span>
                              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Priority · {d.priority}</span>
                            </div>
                            <div className="font-display text-base font-bold text-zinc-900 leading-tight">{d.title}</div>
                            <div className="text-[13px] text-zinc-600 mt-1 leading-relaxed">{d.mitigation}</div>
                            <div className="text-[11px] text-zinc-500 mt-2 flex items-center gap-3 flex-wrap">
                              <span>Owner · <b className="text-zinc-800">{d.owner}</b></span>
                              <span>Due · <b className="text-zinc-800">{d.due}</b></span>
                            </div>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="px-6 py-4 border-t border-zinc-100 flex items-center justify-between bg-zinc-50">
              <div className="text-[12px] text-zinc-500">
                {drafts && `${Object.values(selected).filter(Boolean).length} of ${drafts.length} selected`}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setRemOpen(false)} className="btn btn-secondary btn-sm" data-testid="btn-cancel-remediation">Cancel</button>
                <button
                  onClick={acceptSelected}
                  disabled={!drafts || accepting || Object.values(selected).filter(Boolean).length === 0}
                  className="btn btn-primary btn-sm bg-gradient-to-r from-violet-600 to-blue-600 border-0 text-white disabled:opacity-50"
                  data-testid="btn-accept-remediation"
                >
                  {accepting ? <><Loader2 size={12} className="animate-spin" /> Adding…</> : <>Accept &amp; add to Actions</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
